import calc from "@/helper/calc";
import pdf from "@/helper/pdf";
import axios from "axios";
import axiosInstance from "@/store/api_auth";

const stripHtml = (value) => {
  if (!value) {
    return "";
  }

  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getLocalizedMetafieldValue = (product, key, language) => {
  if (!language) {
    return calc.searchValueInProduct(product, key);
  }

  let defaultValue = calc.searchValueInProduct(product, key);
  let localizedValue = calc.searchValueInProduct(product, key, language);

  if (language === "de") {
    return localizedValue;
  }

  if (!localizedValue || localizedValue === defaultValue) {
    return null;
  }

  return localizedValue;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const buildAssetUrl = (assetPath) =>
  new URL(assetPath, window.location.origin).href;

const LABEL_ASSET_LANGUAGES = ["de", "en", "fr", "it", "nl", "cs"];

const labelAssetLanguage = (language) =>
  LABEL_ASSET_LANGUAGES.includes(language) ? language : "de";

const renderConfiguredProductImageDataUrl = async ({
  name,
  language,
  labelColor,
  capsuleColor,
  labelPattern,
  labelColorHexTop,
  labelColorHexBottom,
  labelFontSize,
}) => {
  const size = 1000;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error(
      "Could not initialize canvas for configured product image."
    );
  }

  if (labelPattern) {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, size, size);
  } else {
    const gradient = context.createLinearGradient(
      0,
      size * 0.75,
      size,
      size * 0.15
    );
    gradient.addColorStop(0, labelColorHexBottom || "#ff0000");
    gradient.addColorStop(0.4, labelColorHexBottom || "#ff0000");
    gradient.addColorStop(0.4, labelColorHexTop || "#f65861");
    gradient.addColorStop(1, labelColorHexTop || "#f65861");
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);
  }

  const labelLanguage = labelAssetLanguage(language);
  const labelAssetPath = labelPattern
    ? `/assets/label/${labelLanguage}/pattern/${labelPattern}.png`
    : `/assets/label/${labelLanguage}/${labelColor}.png`;
  const capsuleAssetPath = `/assets/capsule/${capsuleColor}.png`;

  const [labelImage, capsuleImage] = await Promise.all([
    loadImage(buildAssetUrl(labelAssetPath)),
    loadImage(buildAssetUrl(capsuleAssetPath)),
  ]);

  context.drawImage(labelImage, 0, 0, size, size);
  context.drawImage(capsuleImage, 0, 0, size, size);

  const labelBox = {
    x: 310,
    y: 535,
    width: 210,
    height: 55,
  };
  const labelText = name || "";
  const labelSkew = Math.tan((-20 * Math.PI) / 180);
  let fittedFontSize = Math.min(labelFontSize || 20, 45);

  context.font = `${fittedFontSize}px "Arial", sans-serif`;
  while (
    fittedFontSize > 16 &&
    (context.measureText(labelText).width > labelBox.width - 8 ||
      fittedFontSize > labelBox.height - 8)
  ) {
    fittedFontSize -= 1;
    context.font = `${fittedFontSize}px "Arial", sans-serif`;
  }

  context.save();
  context.translate(
    labelBox.x + labelBox.width / 2,
    labelBox.y + labelBox.height / 2
  );
  context.rotate((-91 * Math.PI) / 180);
  context.transform(1, 0, labelSkew, 1, 0, 0);
  context.beginPath();
  context.rect(
    -labelBox.width / 2,
    -labelBox.height / 2,
    labelBox.width,
    labelBox.height
  );
  context.clip();
  context.fillStyle = "#000000";
  context.font = `${fittedFontSize}px "Arial", sans-serif`;
  context.textBaseline = "middle";
  context.fillText(labelText, -labelBox.width / 2, 0);
  context.restore();

  return canvas.toDataURL("image/png");
};

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const uploadConfiguredProductImage = async ({ dataUrl }) => {
  if (!dataUrl) {
    return null;
  }

  const formData = new FormData();
  const blob = await dataUrlToBlob(dataUrl);
  formData.append("file", blob, "indyvit-configured-product.png");
  formData.append("filename", "indyvit-configured-product.png");
  formData.append("tag", "cover");

  const response = await axiosInstance.post("/public/uploads", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response?.data?.resource?.file_url || response?.data?.file_url || null;
};

export default {
  Order: class Order {
    constructor(id) {
      this.id = id;
      this.name = "";
      this.originalName = "";
      this.labelFontSize = 16;
      this.labelPattern = null;
      this.title = null;
      this.label = null; // was removed from pdf
      this.labelColor = "red";
      this.labelColorHexTop = "#f65861";
      this.labelColorHexBottom = "#ff0000";
      this.capsuleColor = "white";
      this.capsuleColorHex = "#eee";
      this.capsulaMaterial = "Cellulose";
      this.capsulaFill = null;
      this.consent = false;
      this.gender = null;
      this.ageRange = null;
      this.email = null;
      this.newsletter = false;
      this.weight = null;
      this.weightHappyness = null;
      this.height = null;
      this.pregnant = null;
      this.healthGoals = [];
      this.healthPossibleOptions = [];
      this.healthPrio = null;
      this.additionalQuestion = null;
      this.diet = null;
      this.allergic = null;
      this.intolerances = [];
      this.veggyIntake = null;
      this.fishIntake = null;
      this.exercise = null;
      this.bloodPressureDrugs = null;
      this.fragrance = "none";
      this.isCalculated = false;
      this.factors = {};
      this.totalFactor = 0;
      this.personalMix = [];
      this.totalCapsulas = 0;
      this.fixedCapsulasPerDay = null;
      this.intakeRecommendation = null;
      this.intakeRecommendationMin = null;
      this.intakeRecommendationMax = null;
      this.productionSheetCapsulas = null;
      this.productionSheetCapsulasMin = null;
      this.productionSheetCapsulasMax = null;
      this.subdomainConfig = null;
      this.labelStep = 1;
      this.productString = "";
      this.ingredientsOverview = "";
      this.pdfLabelURL = null;
      this.dynamicPdfLabelURL = null;
      this.pdfListURL = null;
      this.labelConfig = null;
      this.productImageUrl = null;
      this.isVisible = true;
      this.isUpselling = false;
      this.apiErrorCounter = 0;
      this.generatePDF = (ctx, branding, language, currency) => {
        return new Promise((resolve, reject) => {
          pdf
            .createPDF(
              this.name,
              this.label,
              this.labelColor,
              this.capsuleColor,
              this.fragrance,
              this.getPersonalMixSortedByIngredientConcentrationMg(),
              this.getPersonalMixSortedByIngredientConcentrationMgAsc(),
              this.getPersonalMixSorttedByCategory(),
              this.totalCapsulas,
              this.capsulaMaterial,
              this.diet,
              branding,
              language,
              ctx,
              this.labelFontSize,
              this.labelPattern,
              {
                intakeRecommendation: this.intakeRecommendation,
                intakeRecommendationMin: this.intakeRecommendationMin,
                intakeRecommendationMax: this.intakeRecommendationMax,
                productionSheetCapsulas: this.productionSheetCapsulas,
                productionSheetCapsulasMin: this.productionSheetCapsulasMin,
                productionSheetCapsulasMax: this.productionSheetCapsulasMax,
                subdomainConfig: this.subdomainConfig,
              }
            )
            .then((pdfData) => {
              if (pdfData) {
                this.labelConfig = this.buildLabelConfig(language);
                this.labelConfig.subdomain_config = this.subdomainConfig;
                pdfData.label_config = this.labelConfig;
                pdfData.subdomain_config = this.subdomainConfig;
                this.productString = pdfData.productString;
                this.ingredientsOverview = pdfData.ingredients_overview;
                let pdfRequest = axios.create({
                  baseURL: `https://api.indyvit.com`,
                  headers: pdf.getAuth(),
                });
                pdfRequest
                  .post("/v1/orders/create_with_url", pdfData)
                  .then(async (response) => {
                    // DEBUG: this will open the pdf label and package list
                    if (localStorage.getItem("debugMode")) {
                      console.log(
                        "Debug mode is set, open label and package pdfs"
                      );
                      window.open(response.data.resource.label_url);
                      window.open(response.data.resource.package_list_url);
                    }
                    this.pdfLabelURL = response.data.resource.label_url;
                    this.dynamicPdfLabelURL =
                      response.data.resource.dynamic_label_url ||
                      response.data.resource.label_url;
                    this.pdfListURL = response.data.resource.package_list_url;
                    this.productImageUrl =
                      response?.data?.resource?.sanitized_images?.[0]?.url ||
                      response?.data?.resource?.image_url ||
                      null;

                    if (!this.productImageUrl) {
                      try {
                        const renderedProductImage =
                          await renderConfiguredProductImageDataUrl({
                            name: this.name,
                            language,
                            labelColor: this.labelColor,
                            capsuleColor: this.capsuleColor,
                            labelPattern: this.labelPattern,
                            labelColorHexTop: this.labelColorHexTop,
                            labelColorHexBottom: this.labelColorHexBottom,
                            labelFontSize: this.labelFontSize,
                          });

                        this.productImageUrl =
                          await uploadConfiguredProductImage({
                            dataUrl: renderedProductImage,
                          });
                      } catch (error) {
                        console.warn(
                          "Failed to render or upload configured product image",
                          error
                        );
                      }
                    }

                    resolve(true);
                  })
                  .catch(() => {
                    if (this.apiErrorCounter < 30) {
                      // retry after 5 seconds
                      setTimeout(() => {
                        this.generatePDF(ctx, branding, language, currency);
                      }, 5 * 1000);
                    } else {
                      reject(false);
                    }
                    this.apiErrorCounter++;
                  });
              }
            });
        });
      };
      this.getPersonalMixSortedByIngredientConcentrationMg = () => {
        // .sort sorts the array in place we need to copy the array
        return [...this.personalMix].sort((a, b) => {
          return b.ingredientConcentrationMg - a.ingredientConcentrationMg;
        });
      };
      this.buildLabelConfig = (language) => {
        return {
          name: this.name,
          original_name: this.originalName,
          language: language || "de",
          label_color: this.labelColor,
          label_pattern: this.labelPattern,
          capsule_color: this.capsuleColor,
          capsula_material: this.capsulaMaterial,
          fragrance: this.fragrance,
          diet: this.diet,
          label_font_size: this.labelFontSize,
          capsulas: this.totalCapsulas,
          intake_recommendation: this.intakeRecommendation,
          intake_recommendation_min: this.intakeRecommendationMin,
          intake_recommendation_max: this.intakeRecommendationMax,
          production_sheet_capsulas: this.productionSheetCapsulas,
          production_sheet_capsulas_min: this.productionSheetCapsulasMin,
          production_sheet_capsulas_max: this.productionSheetCapsulasMax,
          products:
            this.getPersonalMixSortedByIngredientConcentrationMgAsc().map(
              (product) => ({
                item_id: product.id,
                platform_id: product.platform_id,
                name: product.name,
                title: product.title,
                ingredient_name: product.ingredientName,
                ingredient_name_original: product.ingredientNameOriginal,
                amount: product.amount,
                amount_mg: product.amountMg,
                unit: product.unit,
                density: product.density,
                concentration: product.concentration,
              })
            ),
        };
      };
      this.getPersonalMixSortedByIngredientConcentrationMgAsc = () => {
        // .sort sorts the array in place we need to copy the array
        return [...this.personalMix].sort((a, b) => {
          return a.ingredientConcentrationMg - b.ingredientConcentrationMg;
        });
      };
      this.getPersonalMixSorttedByCategory = () => {
        let vitamin = [];
        let minerals = [];
        let veggy = [];
        let all_others = [];
        for (let ing of this.personalMix) {
          switch (ing.category) {
            case "Vitamine":
              vitamin.push(ing);
              break;
            case "Mineralstoffe":
              minerals.push(ing);
              break;
            case "Pflanzliche Stoffe":
              veggy.push(ing);
              break;
            default:
              all_others.push(ing);
              break;
          }
        }
        return vitamin.concat(minerals, veggy, all_others);
      };
      (this.calculateQuiz = (state) => {
        this.personalMix = calc.calculateQuiz(state);
        this.reCalculate(state.currency, {
          monthly: state.monthly,
          reorder: state.reorder,
          isBloodTestChecked: state.isBloodTestChecked,
        });
      }),
        (this.reCalculate = (currency, subscriptionOptions) => {
          // calc all ings
          this.setFillAndCapsulas();
          for (let ing of this.personalMix) {
            ing.calculate(this.totalCapsulas, currency, subscriptionOptions);
          }
        }),
        (this.setFillAndCapsulas = () => {
          this.capsulaFill = calc.capsulaFill(this.personalMix);
          this.totalCapsulas =
            this.fixedCapsulasPerDay || calc.totalCapsulas(this.personalMix);
        });
    }
  },
  Product: class Product {
    constructor(ing, language, totalCapsulas, currency, subscriptionOptions) {
      this.id = ing.id;
      this.platform_id = ing?.["platform_id"];
      this.raw_product_id = ing?.data?.["raw_product_id"];
      this.name = ing[`name_${language}`] || ing.name;
      this.title = ing?.data[`title_${language}`] || ing.data?.title;
      this.description =
        ing?.data[`description_${language}`] || ing?.data?.description;
      this.table = calc.searchValueInProduct(ing, "test_tabelle");

      this.unit = calc.searchValueInProduct(ing, "_einheit_1_");
      this.quizResult =
        ing.quizResult ||
        calc.searchValueInProduct(ing, "regler_rdv", null, true);
      this.amount = this.quizResult;

      // default values -> this.calculate
      this.price = 0;
      this.amountMg = 0;
      this.amountMgInMix = 0;
      this.amountInMix = 0;
      this.weight = 0;
      this.ingredientConcentrationMg = 0;

      this.ingredientName = calc.searchValueInProduct(
        ing,
        "bezeichnung_zutatenliste",
        language
      );
      this.ingredientNameOriginal = calc.searchValueInProduct(
        ing,
        "bezeichnung_zutatenliste"
      );
      this.priceMg = calc.searchValueInProduct(ing, "preis_pro_mg", null, true);
      this.density = calc.searchValueInProduct(ing, "dichte_g_ml_", null, true);
      this.concentration = calc.searchValueInProduct(
        ing,
        "konzentration",
        null,
        true
      );
      this.vegetarisch = calc.searchValueInProduct(ing, "vegetarisch");
      this.vegan = calc.searchValueInProduct(ing, "vegan");
      this.rdv = calc.getRdv(
        this.amount,
        calc.searchValueInProduct(ing, "rdv", null, true)
      );
      this.rdvInital = calc.searchValueInProduct(ing, "rdv", null, true);

      // to show info message if healthgoal was choosen
      this.summary_if_hauhaanae = calc.searchValueInProduct(
        ing,
        "summary_if_hauhaanae",
        language
      );
      this.summary_if_schwanger = calc.searchValueInProduct(
        ing,
        "summary_if_schwanger",
        language
      );
      this.summary_if_fertil_frau = calc.searchValueInProduct(
        ing,
        "summary_if_fertil_frau",
        language
      );
      this.summary_if_herz = calc.searchValueInProduct(
        ing,
        "summary_if_herz",
        language
      );
      this.summary_if_schlaf = calc.searchValueInProduct(
        ing,
        "summary_if_schlaf",
        language
      );
      this.summary_if_menopause = calc.searchValueInProduct(
        ing,
        "summary_if_menopause",
        language
      );
      this.summary_if_konz = calc.searchValueInProduct(
        ing,
        "summary_if_konz",
        language
      );
      this.summary_if_energy = calc.searchValueInProduct(
        ing,
        "summary_if_energy",
        language
      );
      this.summary_if_potenz = calc.searchValueInProduct(
        ing,
        "summary_if_potenz",
        language
      );
      this.summary_if_stimmung = calc.searchValueInProduct(
        ing,
        "summary_if_stimmung",
        language
      );
      this.summary_if_abnehmen = calc.searchValueInProduct(
        ing,
        "summary_if_abnehmen",
        language
      );
      this.summary_if_stress = calc.searchValueInProduct(
        ing,
        "summary_if_stress",
        language
      );
      this.summary_if_vegan = calc.searchValueInProduct(
        ing,
        "summary_if_vegan",
        language
      );
      this.summary_if_fitness = calc.searchValueInProduct(
        ing,
        "summary_if_fitness",
        language
      );
      this.summary_if_raucher = calc.searchValueInProduct(
        ing,
        "summary_if_raucher",
        language
      );
      this.summary_if_immunsystem = calc.searchValueInProduct(
        ing,
        "summary_if_immunsystem",
        language
      );
      this.summary_if_knochen = calc.searchValueInProduct(
        ing,
        "summary_if_knochen",
        language
      );
      this.summary_if_fertil_mann = calc.searchValueInProduct(
        ing,
        "summary_if_fertil_mann",
        language
      );
      this.summary_if_verdauung = calc.searchValueInProduct(
        ing,
        "summary_if_verdauung",
        language
      );
      this.summary_if_longevity = calc.searchValueInProduct(
        ing,
        "summary_if_longevity",
        language
      );
      this.summary_if_alkohol = calc.searchValueInProduct(
        ing,
        "summary_if_alkohol",
        language
      );

      // prices from shopify products (upselling)
      this.min_variant_price = parseFloat(ing?.data?.["min_variant_price"]);
      this.max_variant_price = parseFloat(ing?.data?.["max_variant_price"]);

      this.raw_variant_id = null;
      if (ing?.data?.variants.length > 0) {
        this.raw_variant_id = ing?.data?.variants[0]?.raw_variant_id;
      }

      // get healthtopic for upselling products
      this.healthTopicsString = calc.searchValueInProduct(ing, "healthtopic");
      this.healthTopics = [];
      if (this.healthTopicsString) {
        try {
          this.healthTopics = JSON.parse(this.healthTopicsString);
        } catch (e) {
          console.warn("Error parsing health topic");
        }
      }

      this.firstImage =
        ing?.data?.images.length > 0 ? ing.data.images[0]?.node?.src : null;
      // this.initial = calc.searchValueInProduct(ing, "regler_1_initialwert", true)
      this.reglerRDV = calc.searchValueInProduct(ing, "regler_rdv", null, true);
      this.reglerMax = calc.searchValueInProduct(
        ing,
        "regler_maximalwert",
        null,
        true
      );
      this.minAmount = calc.searchValueInProduct(
        ing,
        "mindestmenge",
        null,
        true
      );
      this.maxAmount = calc.searchValueInProduct(
        ing,
        "regler_maximalwert",
        null,
        true
      );
      this.stepSize =
        calc.searchValueInProduct(ing, "schrittgroesse", null, true) || 10;

      // property names will be used for filtering later on
      this.unvertraeglichkeit_laktose = calc.searchValueInProduct(
        ing,
        "unvertraeglichkeit_laktose"
      );
      this.unvertraeglichkeit_milch = calc.searchValueInProduct(
        ing,
        "unvertraeglichkeit_milch"
      );
      this.unvertraeglichkeit_soja = calc.searchValueInProduct(
        ing,
        "unvertraeglichkeit_soja"
      );
      this.unvertraeg_schalenfruecht = calc.searchValueInProduct(
        ing,
        "unvertraeg_schalenfruecht"
      );
      this.unvertraeglichkeit_fisch = calc.searchValueInProduct(
        ing,
        "unvertraeglichkeit_fisch"
      );
      this.unvertraeglichkeit_gluten = calc.searchValueInProduct(
        ing,
        "unvertraeglichkeit_gluten"
      );
      this.unvertraeglichkeit_erdnuesse = calc.searchValueInProduct(
        ing,
        "unvertraeglichkeit_erdnuesse"
      );
      this.pregnantRecommended = calc.searchValueInProduct(
        ing,
        "pregnant_recommended"
      );

      const localizedDescription =
        ing?.data?.[`description_${language}`] || null;
      const localizedDetails = getLocalizedMetafieldValue(
        ing,
        "was_its",
        language
      );

      this.warningHint = calc.searchValueInProduct(
        ing,
        "warnhinweis",
        language
      );
      this.warningThreshold = calc.searchValueInProduct(
        ing,
        "warnschwellenwert",
        null,
        true
      );
      this.outOfStock = ing?.data?.out_of_stock;
      this.bulletpointDescription = calc.searchValueInProduct(
        ing,
        "bulletpoint_description",
        language
      );
      this.studySituation = calc.searchValueInProduct(
        ing,
        "study_situation",
        language
      );
      this.origin = calc.searchValueInProduct(ing, "origin", language);
      this.benefits = calc.searchValueInProduct(ing, "benefits", language);
      this.details =
        localizedDetails ||
        stripHtml(localizedDescription) ||
        calc.searchValueInProduct(ing, "was_its", language);
      this.point_1 = calc.searchValueInProduct(ing, "nuzen_1", language);
      this.point_2 = calc.searchValueInProduct(ing, "nuzen_2", language);
      this.point_3 = calc.searchValueInProduct(ing, "nuzen_3", language);
      this.shortDescription = calc.searchValueInProduct(
        ing,
        "short_description",
        language
      );

      // ! do not translate this category it should be the german value. Its used for filtering as a key!
      this.category = calc.searchValueInProduct(ing, "kategorie_expertenmodus");
      this.categoryTitle = calc.searchValueInProduct(
        ing,
        "kategorie_expertenmodus",
        language
      );

      // calc price, amountmg, weight
      this.calculate(totalCapsulas || 1, currency, subscriptionOptions);

      // not in use: set bool if kategorie is "Pflanzliche Stoffe"
      if (
        calc.searchValueInProduct(ing, "kategorie_expertenmodus") ==
        "Pflanzliche Stoffe"
      ) {
        this.isCatVeggy = true;
      } else {
        this.isCatVeggy = false;
      }

      // set bool if we need to show rdv
      if (
        calc.searchValueInProduct(ing, "kategorie_expertenmodus") ==
          "Vitamine" ||
        calc.searchValueInProduct(ing, "kategorie_expertenmodus") ==
          "Mineralstoffe"
      ) {
        this.showRDV = true;
      } else {
        this.showRDV = false;
      }

      this.productResponse = ing;
    }
    calculate(totalCapsulas, currency, subscriptionOptions) {
      this.amountMg = calc.amountMg(this.amount, this.unit);
      this.amountMgInMix =
        this.amountMg * (calc.capsulasPerMixForCalculation() / totalCapsulas);
      this.amountInMix = this.amount / totalCapsulas;
      this.weight = calc.getWeight(
        this,
        this.concentration,
        this.density,
        this.unit
      );
      this.ingredientConcentrationMg = this.amountMg / this.concentration;
      this.price = calc.getPrice(
        this,
        this.priceMg,
        totalCapsulas,
        currency,
        subscriptionOptions
      );
      this.rdv = calc.getRdv(this.amount, this.rdvInital);
    }
  },
};
